import { useCallback, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  BarChart3,
  Bell,
  Building2,
  ChevronDown,
  ChevronLeft,
  ClipboardCheck,
  Clock,
  DollarSign,
  FileText,
  HelpCircle,
  Key,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Percent,
  Receipt,
  Server,
  Settings,
  Shield,
  UserCog,
  Users,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { credentialsApi } from "@/lib/api/credentials";
import { useContextStore } from "@/store/contextStore";
import { useAuthStore } from "@/store/authStore";
import { Role } from "@/lib/constants/roles";
import { useNavAccess } from "@/hooks/useNavAccess";
import { useOrgHref } from "@/hooks/useOrgParamSync";
import { useRoleCatalog } from "@/hooks/useRoleCatalog";
import { usePermissions } from "@/hooks/usePermissions";
import { useSettingsStore } from "@/store/settingsStore";
import { useBrandingStore } from "@/store/brandingStore";
import { useThemeStore } from "@/store/themeStore";
import { UserAvatar } from "@/components/ui/user-avatar";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/hint";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SidebarDesk } from "./SidebarDesk";
import { useDeskMe } from "@/features/desk/useDeskCan";
import { SidebarCurrency } from "@/components/layout/SidebarCurrency";
import { ProviderLogo } from "@/components/icons/ProviderLogo";
import {
  connectedProviders,
  type ConnectedProvider,
} from "./connectedProviders";

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  badge?: string | number;
  children?: NavItem[];
}

// Recommendations is deliberately absent. It is not being dropped: the Desk
// roadmap makes recommendations one *source* of Desk items alongside alerts and
// policy violations (D5, "recommendation bridge"), so a standalone page would
// be a second place to look at the same work, competing with the queue that is
// meant to own it. The route and its API stay reachable until the bridge lands.
/** The admin sidebar's rows; a module constant so the nav-access spec can read them. */
// eslint-disable-next-line react-refresh/only-export-components
export const adminNavItems: NavItem[] = [
  { name: "Overview", href: "/admin", icon: LayoutDashboard },
  {
    name: "Platform Analytics",
    href: "/admin/platform-analytics",
    icon: BarChart3,
  },
  // No row for the rate ledger (/admin/rates): rates are set on an
  // organization's detail column in Organizations, which links to it.
  { name: "Organizations", href: "/admin/organizations", icon: Layers },
  { name: "Approvals", href: "/admin/approvals", icon: ClipboardCheck },
  { name: "Users", href: "/admin/users", icon: Users },
  { name: "Groups", href: "/admin/groups", icon: Shield },
  { name: "Impersonation", href: "/admin/impersonation", icon: UserCog },
];

// eslint-disable-next-line react-refresh/only-export-components
export const mainNav: NavItem[] = [
  { name: "Dashboard", href: "/", icon: LayoutDashboard },
  // Billing is a reseller's page: what a distributor bills its MSPs, an MSP
  // its companies. Dimmed rather than hidden for everyone else.
  { name: "Billing", href: "/billing", icon: Receipt, badge: "Beta" },
  // The two cost walks are children rather than siblings. Both start from
  // "what did we spend" and only differ in which axis they walk down -- as
  // top-level rows they read as three unrelated destinations, and a reader
  // looking for spend has to know which of the three holds the number.
  {
    name: "Cost Analysis",
    href: "/costs",
    icon: DollarSign,
    children: [
      {
        name: "Bill Explorer",
        href: "/costs/explorer",
        icon: Building2,
        badge: "Beta",
      },
      { name: "Margin", href: "/costs/margin", icon: Percent },
    ],
  },
  { name: "Inventory", href: "/inventory", icon: Server },
  { name: "Scheduling", href: "/scheduling", icon: Clock },
  { name: "Credentials", href: "/credentials", icon: Key },
  { name: "Reports", href: "/reports", icon: FileText },
  { name: "Alerts", href: "/alerts", icon: Bell },
  { name: "Platform Hub", href: "/capabilities", icon: LayoutGrid },
];

// Admin navigation is now dynamic based on role
// See Sidebar component for construction

/** Only match the exact segment — avoids `/admin` staying active on `/admin/users`. */
function navLinkEnd(href: string, hasChildren = false): boolean {
  // A parent row must match exactly, or NavLink's prefix matching lights it up
  // alongside whichever child is actually open -- two highlighted rows, neither
  // of which tells you where you are. The children own the deeper paths, so the
  // parent only claims its own.
  return href === "/" || href === "/admin" || hasChildren;
}

function SidebarSectionLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  // `.eyebrow` is the shared kicker primitive (globals.css) — display face,
  // 13px/600, .2em tracking, --eyebrow colour. Do not re-specify those here.
  // (The figures drifted: this said 11px/.18em, which the class has not carried
  // for some time. A comment quoting values is only useful while it is right.)
  return <p className={cn("eyebrow mb-3 px-3", className)}>{children}</p>;
}

/**
 * The providers connected inside the selected scope, from the credentials
 * list itself.
 *
 * The list is scope-filtered by the server from the context headers the
 * client already sends. The key carries the selection so a scope change
 * refetches, and the Credentials page, the platform's Connections panel and
 * this well can never disagree about which clouds an organization runs. A
 * static list of six used to sit here, so a company with one Azure
 * subscription saw six marks; that was a picture of the product, not of the
 * account.
 */
function useConnectedProviders() {
  // A role that cannot read cloud accounts is not sent to ask on every page:
  // the answer is a 403, and the rail shows nothing either way.
  const { can } = usePermissions();
  const canReadAccounts = can("services", "view");
  const scope = useContextStore((s) => s.scope);
  const distributorId = useContextStore((s) => s.distributorId);
  const mspId = useContextStore((s) => s.mspId);
  const companyId = useContextStore((s) => s.companyId);
  const query = useQuery({
    queryKey: [
      "credentials",
      "list",
      "sidebar",
      scope,
      distributorId,
      mspId,
      companyId,
    ],
    queryFn: () => credentialsApi.list(),
    staleTime: 60_000,
    enabled: canReadAccounts,
  });
  const providers = useMemo(
    () => connectedProviders(query.data ?? []),
    [query.data],
  );
  // A disabled query stays pending, so the rail draws nothing for that role
  // rather than "No cloud accounts connected", which would be a false zero.
  return { providers, isPending: query.isPending, isError: query.isError };
}

/** Glyph size, in one place: the branches below would otherwise drift apart. */
const PROVIDER_GLYPH = "h-2.5 w-2.5";

function accountsLabel(provider: ConnectedProvider): string {
  return `${provider.name} · ${provider.count} ${
    provider.count === 1 ? "account" : "accounts"
  }`;
}

/**
 * One provider's mark with its account count beside it.
 *
 * The count is the indicator: a mark alone says "this cloud exists here", the
 * number says how much of it. Counts are neutral, so the digits take the
 * panel's foreground and no tone; the tint behind the chip is the provider's
 * own colour at ten percent, which is the one thing being communicated.
 */
function ProviderBadge({
  provider,
  className,
  corner = false,
}: {
  provider: ConnectedProvider;
  className?: string;
  /**
   * Wear the count on the chip's corner instead of beside the mark. For the
   * folded rail, where two square chips fit across and a wide chip per row
   * ran the well past the scroll area at a laptop height.
   */
  corner?: boolean;
}) {
  if (corner) {
    // No hover text of its own: the folded stack's button already opens one
    // tooltip listing every provider with its count.
    return (
      <span
        role="img"
        aria-label={accountsLabel(provider)}
        className={cn(
          "relative inline-flex h-5 w-5 items-center justify-center rounded",
          provider.bgClass,
          className,
        )}
      >
        <ProviderLogo provider={provider.id} className={PROVIDER_GLYPH} />
        <span className="absolute -right-1 -top-1 min-w-[13px] rounded-full border border-sidebar bg-muted px-[3px] text-center font-mono text-[8px] leading-[11px] tabular-nums text-foreground">
          {provider.count > 99 ? "99+" : provider.count}
        </span>
      </span>
    );
  }
  return (
    <Hint content={accountsLabel(provider)}>
      <span
        role="img"
        aria-label={accountsLabel(provider)}
        className={cn(
          // No border: at this size a hairline round every chip stacked up into
          // its own texture and fought the panel's own edge.
          "inline-flex h-5 items-center gap-1 rounded pl-1 pr-1.5",
          provider.bgClass,
          className,
        )}
      >
        <ProviderLogo provider={provider.id} className={PROVIDER_GLYPH} />
        <span className="font-mono text-[10px] leading-none tabular-nums text-sidebar-foreground/80">
          {provider.count}
        </span>
      </span>
    </Hint>
  );
}

function CollapsedProviderStack({
  providers,
}: {
  providers: ConnectedProvider[];
}) {
  const total = providers.reduce((sum, p) => sum + p.count, 0);
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="mx-auto grid w-full grid-cols-2 justify-items-center gap-x-2 gap-y-1.5 rounded-md border border-sidebar-border/40 bg-sidebar-accent/20 px-1 pb-1 pt-1.5 outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={`${total} connected ${total === 1 ? "account" : "accounts"} across ${providers.length} ${providers.length === 1 ? "provider" : "providers"}`}
        >
          {providers.map((provider) => (
            <ProviderBadge key={provider.id} provider={provider} corner />
          ))}
        </button>
      </TooltipTrigger>
      {/* Opaque, like every floating layer: a translucent pane over the rail
          shows the nav rows through the list. */}
      <TooltipContent
        side="right"
        align="center"
        sideOffset={12}
        className="rounded-lg border border-border bg-popover p-3"
      >
        <p className="eyebrow mb-2">Providers</p>
        <dl className="grid grid-cols-[auto_1fr_auto] items-center gap-x-2.5 gap-y-1.5">
          {providers.map((provider) => (
            <div key={provider.id} className="contents">
              <span
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded-md",
                  provider.bgClass,
                )}
              >
                <ProviderLogo provider={provider.id} className="h-3 w-3" />
              </span>
              <dt className="text-xs font-medium text-foreground">
                {provider.name}
              </dt>
              <dd className="font-mono text-[11px] tabular-nums text-muted-foreground">
                {provider.count}
              </dd>
            </div>
          ))}
        </dl>
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarProviders({ collapsed }: { collapsed: boolean }) {
  const { providers, isPending, isError } = useConnectedProviders();

  // Nothing while the list is on its way: a well that flashes empty and then
  // fills reads as a glitch. A failed read is also silence here, because the
  // rail is not where a credentials error gets explained; the Credentials
  // page owns that state.
  if (isPending || isError) return null;

  if (collapsed) {
    if (providers.length === 0) return null;
    return (
      <div className="border-t border-sidebar-border pt-2">
        <CollapsedProviderStack providers={providers} />
      </div>
    );
  }

  return (
    // Held back deliberately: this is a status readout sitting under the
    // navigation, so its chrome is muted to keep it from competing with rows
    // people actually click. Only the frame recedes — the logos keep their
    // brand colour, which is the one thing being communicated here.
    <div className="mt-5 space-y-2">
      <SidebarSectionLabel className="mb-0 opacity-60">
        Providers
      </SidebarSectionLabel>
      {providers.length === 0 ? (
        // Absence is not zero: an account with nothing connected is told so,
        // rather than shown an empty frame it might take for a loading state.
        <p className="px-3 text-[11px] text-sidebar-muted">
          No cloud accounts connected
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-sidebar-border/40 bg-sidebar-accent/20 px-2 py-1.5">
          {providers.map((provider) => (
            <ProviderBadge key={provider.id} provider={provider} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A parent row with its children folded underneath.
 *
 * The chevron is a separate control from the label rather than the whole row
 * being a toggle. The parent is a real destination -- `/costs` is a page, not
 * just a heading -- so a row that only expanded would strand it, and a row that
 * only navigated could never be closed again. Splitting them lets the label go
 * to the page and the chevron open the group, which is also why the chevron is
 * a `<button>` beside the link instead of inside it: an interactive control
 * nested in an anchor is invalid and swallows its own clicks.
 */
function NavSection({
  item,
  collapsed,
  open,
  onToggle,
}: {
  item: NavItem;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  // With the rail collapsed there is no room for children and no label to hang
  // them under, so the section degrades to its parent row.
  if (collapsed) {
    return <NavItem item={item} collapsed />;
  }

  const sectionId = `nav-section-${item.href.replace(/\W+/g, "-")}`;

  return (
    <div>
      <div className="flex items-center gap-0.5">
        <div className="min-w-0 flex-1">
          <NavItem item={item} collapsed={false} />
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={sectionId}
          aria-label={`${open ? "Collapse" : "Expand"} ${item.name}`}
          className="rounded-md p-1.5 text-sidebar-muted transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
        >
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
            )}
          />
        </button>
      </div>
      {open && (
        <div id={sectionId} className="mt-0.5 space-y-0.5">
          {item.children?.map((child) => (
            <NavItem key={child.name} item={child} collapsed={false} indented />
          ))}
        </div>
      )}
    </div>
  );
}

function NavItem({
  item,
  collapsed,
  indented,
}: {
  item: NavItem;
  collapsed: boolean;
  indented?: boolean;
}) {
  // The scope bar's selection rides on the link, so a row opened in a new tab
  // lands on the same narrowed list. Matching ignores the query string, so
  // the row still lights up on its page.
  const carry = useOrgHref();
  return (
    // Folded, the row is a bare icon, so its name is the hint beside it.
    <Hint content={collapsed ? item.name : undefined} side="right">
      <NavLink
        to={carry(item.href)}
        end={navLinkEnd(item.href, Boolean(item.children?.length))}
        aria-label={collapsed ? item.name : undefined}
        // A plain string keyed off `aria-current`, not NavLink's function
        // form: under the hint's trigger slot a function className is turned
        // into its own source text and every class on the row is lost.
        // NavLink sets `aria-current="page"` on the active row.
        className={cn(
          // No left border: a 2px rule on a rounded row traces the corner radius
          // and renders as a crescent down the left edge — a "fingernail". The
          // selected state is carried by the tinted surface plus the accent
          // colour on the label and icon, which is quieter and unambiguous.
          "group flex items-center rounded-md text-sm transition-colors duration-200",
          collapsed
            ? "w-full justify-center py-2.5"
            : indented
              ? "gap-3 py-2 pl-9 pr-3 text-[13px]"
              : "gap-3 px-3 py-2.5",
          "font-medium text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground",
          "aria-[current=page]:bg-primary/10 aria-[current=page]:font-semibold aria-[current=page]:text-primary",
          "aria-[current=page]:hover:bg-primary/10 aria-[current=page]:hover:text-primary",
        )}
      >
        <item.icon
          className={cn("shrink-0", indented ? "h-4 w-4" : "h-[18px] w-[18px]")}
        />
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 break-words">{item.name}</span>
            {item.badge && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">
                {item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>
    </Hint>
  );
}

export function Sidebar({
  inDrawer = false,
}: {
  /**
   * Rendered inside `NavDrawer`, below `md`, rather than as the fixed rail.
   * The drawer is always the open rail: it has the room, and folding it to
   * icons behind a menu button would hide the names twice over.
   */
  inDrawer?: boolean;
} = {}) {
  const { sidebarCollapsed: railCollapsed, toggleSidebar } = useSettingsStore();
  const sidebarCollapsed = inDrawer ? false : railCollapsed;
  // Desk is a permission module and `view` is its gate. Until the answer is in,
  // the launcher is offered as usual; once it says no, the row is hidden, the
  // same rule every other row follows now (see `useNavAccess`): a destination
  // that refuses to open reads as a broken account, not as a product with more
  // in it.
  const { data: deskMe } = useDeskMe();
  const deskLock =
    deskMe && !deskMe.actions.includes("view")
      ? "Desk is not switched on for you"
      : undefined;
  const branding = useBrandingStore();
  const isDark = useThemeStore((s) => s.getEffectiveMode()) === "dark";
  const brandName =
    branding.enabled && branding.productName
      ? branding.productName
      : "EverythingCloud";
  const brandLogo = branding.enabled
    ? ((isDark ? branding.logoDark : branding.logo) ?? branding.logo)
    : null;
  const { user } = useAuthStore();
  const { visibleNav } = useNavAccess();
  const location = useLocation();
  const {
    catalog: roleCatalog,
    isAdminTier,
    isLoading: roleCatalogLoading,
  } = useRoleCatalog();
  const isSystemAdmin =
    user?.role === "platform_admin" || user?.role === "platform_manager";
  const isDistributor = user?.role === "distributor_admin";
  const isMSPAdmin = user?.role === "msp_admin";
  // Any admin-tier role (system / distributor / MSP / company owner /
  // company admin) gets access to the Admin Panel. Source of truth is the
  // server-authoritative role catalog (``is_admin_tier`` flag) so a new
  // admin role added in ``User.ROLE_CHOICES`` flows through automatically.
  // Per-route gates below still hide platform-wide sections from lower tiers.
  //
  // On a cold refresh the catalog is fetched async and ``isAdminTier`` returns
  // false until it lands — which would render the main sidebar for a frame and
  // then swap to the admin sidebar (visible flash on /admin/* refresh). While
  // the catalog is still loading, fall back to a local admin-role check derived
  // from the synchronously-rehydrated JWT role. Once the catalog resolves the
  // authoritative value takes over (the local set mirrors ``is_admin_tier`` so
  // there's no visible change).
  const localAdminTier =
    isSystemAdmin ||
    isDistributor ||
    isMSPAdmin ||
    user?.role === Role.COMPANY_OWNER ||
    user?.role === Role.COMPANY_ADMIN;
  // Falls back to the role code whenever the catalog is unavailable — a failed
  // fetch must not strip a genuine admin's nav. Server still enforces access.
  const roleCatalogUnavailable = roleCatalogLoading || roleCatalog.length === 0;
  const isAdmin = roleCatalogUnavailable
    ? localAdminTier
    : isAdminTier(user?.role);
  const isAdminRoute = location.pathname.startsWith("/admin");
  // Hidden, not dimmed (TW-45774919). These rows used to stay visible and
  // greyed out, on the reasoning that the shape of the product should be
  // legible and people should know what to ask for. Testing came back the other
  // way: a sidebar full of destinations that refuse to open reads as a broken
  // account rather than as a product with more in it, and it advertises
  // administrative surfaces to people who have no business seeing they exist.
  //
  // The rule lives in `useNavAccess` because the command palette has to answer
  // the same question, and two lists drift.
  const mainNavGated = useMemo(() => visibleNav(mainNav), [visibleNav]);

  const adminNav: NavItem[] = useMemo(
    () => visibleNav(adminNavItems),
    [visibleNav],
  );

  const navigation = isAdminRoute && isAdmin ? adminNav : mainNavGated;

  /**
   * Sections are folded by default and remembered only for this mount.
   *
   * Deliberately not persisted: which groups a reader had open is a scratch
   * preference, and restoring it from storage means a reload can put the nav in
   * a state the reader does not remember choosing.
   */
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const toggleSection = useCallback((name: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (!next.delete(name)) next.add(name);
      return next;
    });
  }, []);

  /**
   * A section holding the current page is open regardless of the toggle.
   *
   * This is what makes a pasted link land somewhere legible: without it, opening
   * `/costs/breakdown` directly would highlight a child row inside a collapsed
   * parent -- the reader's own location hidden from them. It also means the
   * section you are inside cannot be folded away, which is the correct
   * asymmetry: collapsing the group you are standing in only removes context.
   */
  const isSectionOpen = useCallback(
    (item: NavItem) =>
      openSections.has(item.name) ||
      (item.children ?? []).some((child) =>
        location.pathname.startsWith(child.href),
      ),
    [openSections, location.pathname],
  );

  // In the drawer the panel is a plain box filling the sheet: a second
  // complementary landmark inside a dialog says nothing, and the glass themes
  // restyle every `aside` with a border of its own beside the sheet's.
  const Shell = inDrawer ? "div" : "aside";

  return (
    <TooltipProvider>
      <Shell
        className={cn(
          inDrawer
            ? "flex h-full flex-col bg-sidebar"
            : // Separation from the canvas comes from the border alone. A drop
              // shadow is invisible against a near-black page and only muddies
              // the light theme, so there is none.
              // `top` is driven by --impersonation-bar-h (set on <html> by
              // ImpersonationBar, 0px when hidden) rather than `inset-y-0`. The
              // sidebar is fixed, so a parent's padding cannot move it — without
              // this it starts at the viewport top and sits over the bar.
              // Hidden below `md`, where `NavDrawer` carries it instead: a
              // fixed 256px rail on a phone leaves the page a sliver.
              "fixed bottom-0 left-0 top-[var(--impersonation-bar-h,0px)] z-50 hidden flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-300 ease-in-out md:flex",
          !inDrawer && (sidebarCollapsed ? "w-16" : "w-64"),
        )}
      >
        {/* Logo Section — distributor branding overrides the default mark/name.
            In the drawer the right padding keeps a long brand name clear of
            the sheet's close button. */}
        <div
          className={cn(
            "flex h-16 items-center gap-3 px-4 border-b border-sidebar-border",
            inDrawer && "pr-12",
          )}
        >
          <div className="logo-container flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-card p-1">
            <img
              src={brandLogo ?? "/logo.png"}
              alt={brandName}
              className="h-full w-full object-contain"
            />
          </div>
          {!sidebarCollapsed && (
            <div className="flex flex-col min-w-0">
              {/* Wrapped, not cut: a distributor's brand name is theirs to
                  choose, and a trimmed one is someone else's product. */}
              <span className="break-words text-sm font-semibold text-sidebar-foreground">
                {brandName}
              </span>
              <span className="text-[10px] text-sidebar-muted">
                FinOps Platform
              </span>
            </div>
          )}
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 scrollbar-thin">
          {!sidebarCollapsed && (
            <SidebarSectionLabel>
              {isAdminRoute ? "Administration" : "Menu"}
            </SidebarSectionLabel>
          )}

          <div className="space-y-1">
            {navigation.map((item) =>
              item.children?.length ? (
                <NavSection
                  key={item.name}
                  item={item}
                  collapsed={sidebarCollapsed}
                  open={isSectionOpen(item)}
                  onToggle={() => toggleSection(item.name)}
                />
              ) : (
                <NavItem
                  key={item.name}
                  item={item}
                  collapsed={sidebarCollapsed}
                />
              ),
            )}
          </div>

          {/* Admin Link - only show on main routes for admins.
              Kept above Providers so every destination in the rail is grouped
              together; Providers is a status readout, not somewhere to go, and
              a link stranded beneath it reads as part of that display. */}
          {isAdmin && !isAdminRoute && (
            <div className="mt-6">
              {!sidebarCollapsed && (
                <SidebarSectionLabel>Administration</SidebarSectionLabel>
              )}
              <NavItem
                item={{ name: "Admin Panel", href: "/admin", icon: Shield }}
                collapsed={sidebarCollapsed}
              />
            </div>
          )}

          {!isAdminRoute && <SidebarProviders collapsed={sidebarCollapsed} />}

          {/* Leaving the admin area is an exit, not another destination, so it
              sits under a rule with no section label. An eyebrow reading
              "Navigation" announced a single row — heavier than the row itself,
              and naming a category every other row in the rail belongs to.

              The icon stays the dashboard mark rather than a back-arrow: the
              collapsed rail shows icons alone, and a chevron here would be a
              second one pointing the same way as the collapse toggle. */}
          {isAdminRoute && (
            <div className="mt-4 border-t border-sidebar-border/60 pt-3">
              <NavItem
                item={{
                  name: "Back to Dashboard",
                  href: "/",
                  icon: LayoutDashboard,
                }}
                collapsed={sidebarCollapsed}
              />
            </div>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-sidebar-border p-3 space-y-1">
          {/* Settings, and the reporting currency sharing its row.
              The currency states the unit every figure on every page is
              denominated in, so it has to be readable without opening
              anything — `$` alone does not distinguish USD from CAD. But a
              full-width row of its own gave a preference the same weight as a
              destination, in the part of the rail the eye scans for
              destinations. Beside Settings it stays permanently visible and
              reads as what it is.

              Siblings in a flex row, never nested: Settings is a link and the
              currency is a menu trigger, and an interactive control inside an
              anchor is neither clickable nor announced correctly.

              Collapsed, the rail is icons only and there is no room to split a
              row, so the two stack as before. */}
          {sidebarCollapsed ? (
            <>
              {deskLock ? null : <SidebarDesk collapsed />}
              <NavItem
                item={{ name: "Settings", href: "/settings", icon: Settings }}
                collapsed
              />
              <SidebarCurrency collapsed />
            </>
          ) : (
            <>
              {/* Desk gets its own row rather than sharing with Settings: it is
                  a way out of this application, not a preference, and a bare
                  icon wedged beside two other controls would not say so. */}
              {deskLock ? null : <SidebarDesk collapsed={false} />}
              <div className="flex items-center gap-1.5">
                <div className="min-w-0 flex-1">
                  <NavItem
                    item={{
                      name: "Settings",
                      href: "/settings",
                      icon: Settings,
                    }}
                    collapsed={false}
                  />
                </div>
                <SidebarCurrency collapsed={false} compact />
              </div>
            </>
          )}

          {/* Help */}
          <NavItem
            item={{ name: "Help & Support", href: "/help", icon: HelpCircle }}
            collapsed={sidebarCollapsed}
          />

          {/* User Profile */}
          <div
            className={cn(
              "mt-3 flex items-center gap-3 rounded-lg p-2",
              sidebarCollapsed ? "justify-center" : "bg-sidebar-accent/50",
            )}
          >
            <UserAvatar
              seed={user?.email ?? ""}
              src={user?.avatar}
              name={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()}
              size={36}
              className="shrink-0"
            />
            {!sidebarCollapsed && (
              // Wrapped, not truncated: the rail is narrow, and a cut email
              // loses its domain, which is the part that says whose account
              // this is.
              <div className="flex-1 min-w-0">
                <p className="break-words text-sm font-medium text-sidebar-foreground">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="break-words text-[11px] text-sidebar-muted">
                  {user?.email}
                </p>
              </div>
            )}
          </div>

          {/* Actions Row. Not in the drawer: folding is the rail's, and the
              drawer closes from its own button, the backdrop or Escape. */}
          {!inDrawer && (
            <div
              className={cn(
                "flex items-center gap-1 pt-2",
                sidebarCollapsed ? "flex-col" : "",
              )}
            >
              {/* Collapse Toggle */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleSidebar}
                    // Folded, this is a bare chevron, and the tooltip describes
                    // it only while open, so it needs a name of its own.
                    aria-label={sidebarCollapsed ? "Expand sidebar" : undefined}
                    className={cn(
                      "h-8 text-sidebar-muted hover:text-sidebar-foreground",
                      sidebarCollapsed ? "w-8 p-0" : "flex-1",
                    )}
                  >
                    <ChevronLeft
                      className={cn(
                        "h-4 w-4 transition-transform duration-200",
                        sidebarCollapsed && "rotate-180",
                      )}
                    />
                    {!sidebarCollapsed && (
                      <span className="ml-2 text-xs">Collapse</span>
                    )}
                  </Button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right">Expand sidebar</TooltipContent>
                )}
              </Tooltip>
            </div>
          )}
        </div>
      </Shell>
    </TooltipProvider>
  );
}
